"use client"

import { useState, useEffect } from "react"

interface TourTriggerButtonProps {
  onStartTour: () => void
  onDismiss: () => void
  isMobile: boolean
}

export function TourTriggerButton({ onStartTour, onDismiss, isMobile }: TourTriggerButtonProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Fade in animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true)
      // Start bounce animation after fade in
      setTimeout(() => setIsAnimating(true), 300)
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  if (isMobile) {
    // Mobile: centered button
    return (
      <div
        className={`fixed z-[100] left-1/2 -translate-x-1/2 transition-all duration-500 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
        style={{
          bottom: 110,
        }}
      >
        {/* The dismiss control is a sibling, not a child, of the main button -
            nesting a <button> inside a <button> is invalid HTML, and the
            browser silently un-nests it during parsing, which both triggers a
            hydration mismatch and leaves the dismiss control unclickable. */}
        <div
          className={`relative ${isAnimating ? "animate-bounce" : ""}`}
          style={{ animationDuration: "2s" }}
        >
          <button
            onClick={onStartTour}
            className="relative flex items-center gap-2 bg-white text-black pl-4 pr-10 py-2.5 rounded-full shadow-lg border-2 border-blue-500"
          >
            {/* Question mark icon */}
            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">?</span>
            </div>
            <div className="text-left">
              <div className="font-bold text-sm">How to play</div>
              <div className="text-xs text-gray-600">Learn to play Pelf.</div>
            </div>
          </button>

          {/* Dismiss button */}
          <button
            onClick={onDismiss}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-black hover:bg-black/10 transition-colors"
            aria-label="Dismiss"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    )
  }

  // Desktop: centered button
  return (
    <div
      className={`fixed z-[100] left-1/2 -translate-x-1/2 transition-all duration-500 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={{
        bottom: 110,
      }}
    >
      <div
        className={`relative ${isAnimating ? "animate-bounce" : ""}`}
        style={{ animationDuration: "2s" }}
      >
        {/* Glow effect */}
        <div className="absolute inset-0 bg-blue-500 rounded-xl blur-lg opacity-30 animate-pulse" />

        {/* Dismiss is a sibling of the main button - see the mobile branch */}
        <button
          onClick={onStartTour}
          className="relative flex items-center gap-3 bg-white text-black pl-5 pr-12 py-3 rounded-xl shadow-xl border-2 border-blue-500 hover:scale-105 transition-transform"
        >
          {/* Question mark icon */}
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white font-bold text-lg">?</span>
          </div>
          <div className="text-left">
            <div className="font-bold text-sm">How to play</div>
            <div className="text-xs text-gray-600">Learn to play Pelf.</div>
          </div>
        </button>

        {/* Dismiss button */}
        <button
          onClick={onDismiss}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-black hover:bg-black/10 transition-colors"
          aria-label="Dismiss"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  )
}
